import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  ICONS,
  ButtonsWrapper,
  Button,
  TYPOGRAPHY_SIZE,
  PLATFORM_THEME_COLORS,
  PageLayout,
  DeleteModal,
  Loader,
} from '@thinknet-gmbh/platform-common-ui';
import { ProjectStatusesEnum, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import { PROJECT_MODALS } from 'src/pages/projects/projects.constants';
import { ROUTES } from 'src/components/app/app-routes.constants';
import { PROJECTS_SUB_PAGES } from 'src/pages/projects/sub-pages/create-project/create-project.constants';
import { useGetProjectById } from 'src/hooks/projects/get-project-by-id.hook';
import { useChangeProjectStatus } from 'src/hooks/projects/change-project-status.hook';
import { useUserCanManageDocument } from 'src/hooks/user-can-manage-document.hook';
import { TaskTemplatesModal } from 'src/pages/projects/components/task-templates-modal';

import { ProjectDetailsEdit } from './components/project-details-edit';
import { ProjectDetailsInfo } from './components/project-details-info';
import { HeaderContainer } from './project-details.styles';

const ProjectDetails = () => {
  const [isEdit, setIsEdit] = useState(false);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [selectedTemplatesIds, setSelectedTemplatesIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'projects']);
  const { id } = useParams();

  const { project, loading } = useGetProjectById({ projectId: id as string });
  const { canEdit } = useUserCanManageDocument({
    createdBy: project.createdBy,
    platformPermission: PLATFORM_PERMISSIONS.projectManagement,
  });

  const changeProjectStatus = useChangeProjectStatus({ onSuccess: () => setActiveModal(null) });

  const onSubmitFinishProject = () => {
    changeProjectStatus({
      variables: {
        payload: {
          projectId: project._id,
          status: ProjectStatusesEnum.FINISHED,
        },
      },
    });
  };

  const onSubmitTaskTemplates = (values: any) => {
    setSelectedTemplatesIds([]);
    setSelectedTemplatesIds(values.taskTemplatesIds);
    setActiveModal(null);
  };

  return (
    <PageLayout pageTitle={t('projects:labels.projectProfile')}>
      {activeModal === PROJECT_MODALS.finishProject && (
        <DeleteModal
          title={t('projects:modals.finishProject.title')}
          subTitle={t('projects:modals.finishProject.subtitle')}
          onSubmit={onSubmitFinishProject}
          submitBtnText={t('projects:buttons.finish')}
          offDanger
          translate={t}
          onClose={() => setActiveModal(null)}
          isOpened
        />
      )}
      {activeModal === PROJECT_MODALS.useTaskTemplate && (
        <TaskTemplatesModal onClose={() => setActiveModal(null)} onFinish={onSubmitTaskTemplates} />
      )}

      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('projects:labels.projectDetails')}
        </Typography>

        <ButtonsWrapper>
          {isEdit && (
            <Button
              type="primary"
              ghost
              icon={ICONS.folderPlus}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={() => setActiveModal(PROJECT_MODALS.useTaskTemplate)}
            >
              {t('projects:buttons.useTaskTemplates')}
            </Button>
          )}
          <Button
            type="primary"
            ghost
            icon={isEdit ? ICONS.task : ICONS.bookContent}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={
              isEdit
                ? () => setActiveModal(PROJECT_MODALS.finishProject)
                : () => navigate(`${ROUTES.project}/${id}/${PROJECTS_SUB_PAGES.changelog}`)
            }
          >
            {isEdit ? t('projects:buttons.finishProject') : t('projects:buttons.changelog')}
          </Button>
          {canEdit && project.status === ProjectStatusesEnum.ACTIVE && !isEdit && (
            <Button
              type="primary"
              ghost
              icon={ICONS.editAlt}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={() => {
                setSelectedTemplatesIds([]);
                setIsEdit(true);
              }}
            >
              {t('common:buttons.edit')}
            </Button>
          )}
        </ButtonsWrapper>
      </HeaderContainer>
      {loading ? (
        <Loader />
      ) : isEdit ? (
        <ProjectDetailsEdit
          project={project}
          taskTemplatesIds={selectedTemplatesIds}
          onCancel={() => setIsEdit(false)}
        />
      ) : (
        <ProjectDetailsInfo project={project} />
      )}
    </PageLayout>
  );
};

export default ProjectDetails;
